import { createContext, Dispatch, SetStateAction, useContext } from 'react';

export type PaginationKey =
  | 'paginationDossier'
  | 'paginationSupervisionComptes'
  | 'paginationAdministrationComptes'
  | 'paginationAdministrationAF'
  | 'paginationAdministrationCentreVHU'
  | 'paginationAdministrationEtats'
  | 'paginationAdministrationFourrieres'
  | 'paginationAdministrationGenresSimplifie'
  | 'paginationAdministrationMarques'
  | 'paginationAdministrationMotifsMef'
  | 'paginationAdministrationUnitesFO'
  | 'paginationAdministrationMessagesInformation'
  | 'paginationAdministrationJuridictions'
  | 'paginationFluxAntai'
  | 'paginationFluxRpsi'
  | 'paginationFluxAlienationEtatBiens'
  | 'paginationFluxAlienationEntitesRemettantes'
  | 'paginationFluxAlienationLieuxDepots'
  | 'paginationConfiguration';

type TablePagination = {
  page: number;
  pageSize: number;
};

export type DictTablePagination = {
  [KeyPagination in PaginationKey]: TablePagination;
};

const defaultPagination: TablePagination = {
  page: 1,
  pageSize: 10,
};

export const PAGINATION_DICT_DEFAULT: DictTablePagination = {
  paginationDossier: defaultPagination,
  paginationSupervisionComptes: defaultPagination,
  paginationAdministrationComptes: defaultPagination,
  paginationAdministrationAF: defaultPagination,
  paginationAdministrationCentreVHU: defaultPagination,
  paginationAdministrationEtats: defaultPagination,
  paginationAdministrationFourrieres: defaultPagination,
  paginationAdministrationGenresSimplifie: defaultPagination,
  paginationAdministrationMarques: defaultPagination,
  paginationAdministrationMotifsMef: defaultPagination,
  paginationAdministrationUnitesFO: defaultPagination,
  paginationAdministrationMessagesInformation: defaultPagination,
  paginationAdministrationJuridictions: defaultPagination,
  paginationFluxAntai: defaultPagination,
  paginationFluxRpsi: defaultPagination,
  paginationFluxAlienationEtatBiens: defaultPagination,
  paginationFluxAlienationEntitesRemettantes: defaultPagination,
  paginationFluxAlienationLieuxDepots: defaultPagination,
  paginationConfiguration: defaultPagination,
};

interface PaginationContextContent<TablePagination> {
  pagination: TablePagination;
  setPagination: Dispatch<SetStateAction<TablePagination>>;
}

const PaginationContext =
  createContext<PaginationContextContent<DictTablePagination> | null>(null);

export const usePaginationContext = (
  keyPagination: PaginationKey,
): [TablePagination, Dispatch<SetStateAction<TablePagination>>] => {
  const context = useContext(PaginationContext);
  if (context === null) {
    throw new Error('usePaginationContext called outside Context.');
  }

  const setPagination: Dispatch<SetStateAction<TablePagination>> = (
    newPagination: SetStateAction<TablePagination>,
  ) => {
    context.setPagination(oldValue => {
      const newValue = { ...oldValue };

      if (typeof newPagination === 'function') {
        newValue[keyPagination] = newPagination(newValue[keyPagination]);
      } else {
        newValue[keyPagination] = newPagination;
      }

      return newValue;
    });
  };

  return [context.pagination[keyPagination], setPagination];
};

const PaginationProvider = PaginationContext.Provider;

export default PaginationProvider;
